import React, { useEffect, useRef } from 'react';

import { EnvironmentRead } from '@ariksa/compliance-policies';
import { AlertWorkflowResponse } from '@ariksa/notification/api';
import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import { filter, includes, toArray } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { colorOpacity, customTheme } from 'theme';

import { getIcon, StackedHeader } from 'components/DataDisplay';
import { Form } from 'components/DataEntry';
import { ArrowAttachIcon, ArrowDetachIcon } from 'components/Icons';
import { Modal } from 'components/Overlay';
import { selectBlueprints } from 'containers/PolicyHub/Blueprints/selectors';
import { actions } from 'containers/PolicyHub/Blueprints/slice';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';

interface Props {
  isOpen: boolean;
  onClose();
  action: 'Attach' | 'Detach';
  environment: EnvironmentRead;
  attachedWorkflows: AlertWorkflowResponse[];
}

export const AttachDetachWorkflow = (props: Props) => {
  const { isOpen, onClose, action, environment, attachedWorkflows } = props;
  const { blueprint, blueprintAction } = useSelector(selectBlueprints);
  const { workflows } = useSelector(selectSharedState);
  const dispatch = useDispatch();
  const okButtonRef = useRef<HTMLButtonElement>(null);
  const [workflowOptions, setWorkflowOptions] = React.useState<
    Record<string, any>[]
  >([]);
  const [filteredWorkflows, setFilteredWorkflows] = React.useState<
    AlertWorkflowResponse[]
  >(attachedWorkflows);
  const [selectedWorkflow, setSelectedWorkflow] = React.useState<
    Record<string, any>
  >({});

  useEffect(() => {
    dispatch(sharedStateActions.getWorkflows({}));
  }, [dispatch]);

  useEffect(() => {
    if (action === 'Attach') {
      const ids = map(attachedWorkflows, o => o.uuid);
      setFilteredWorkflows(filter(workflows.data, o => !includes(ids, o.uuid)));
    } else setFilteredWorkflows(attachedWorkflows);
  }, [action, attachedWorkflows, workflows.data]);

  useEffect(() => {
    const options = map(filteredWorkflows, o => ({
      label: o.name,
      value: o.uuid,
      data: o,
    }));
    setSelectedWorkflow(options[0]);
    setWorkflowOptions(options);
  }, [filteredWorkflows]);

  const handleSubmit = data => {
    const payload = {
      policy_id: blueprint.data?.id,
      environment_id: environment?.id,
    };
    const onSuccess = () => {
      dispatch(
        actions.getAttachedWorkflows({ q: { policyId: blueprint.data?.id } }),
      );
      onClose();
    };
    if (action === 'Attach') {
      dispatch(
        actions.attachWorkflow({
          q: {
            workflowId: selectedWorkflow?.value,
            addWorkflowPolicyRequest: payload,
          },
          onSuccess,
        }),
      );
    } else
      dispatch(
        actions.detachWorkflow({
          q: {
            workflowId: selectedWorkflow?.value,
            removeWorkflowPolicyRequest: payload,
          },
          onSuccess,
        }),
      );
  };

  const handleOkKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    event.currentTarget.click();
  };

  const renderFormInputBox = value => {
    return (
      <Box
        border="1px solid"
        borderColor="componentBorder"
        px={4}
        py={1}
        borderRadius={6}
      >
        {value}
      </Box>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      initialFocusRef={okButtonRef}
      header={
        <StackedHeader
          upper={(action === 'Attach' ? 'Attach' : 'Detach') + ' Workflow'}
          lower={blueprint?.data?.name}
          useCustomColor
          color={
            action === 'Attach' ? customTheme.colors.green['300'] : 'critical'
          }
          icon={action === 'Attach' ? <ArrowAttachIcon /> : <ArrowDetachIcon />}
        />
      }
      body={
        <>
          <Form
            schema={{
              environment: {
                type: 'custom-with-form-control',
                label: 'Environment To Use For',
                component: renderFormInputBox(environment.name),
              },
              workflow: {
                type: 'react-select',
                label:
                  'Workflow To ' + (action === 'Attach' ? 'Execute' : 'Detach'),
                options: workflowOptions,
                value: selectedWorkflow,
                onChange: setSelectedWorkflow,
                isLoading: workflows.isLoading,
              },
              targets: {
                type: 'custom-with-form-control',
                label: 'Targets',
                component: (
                  <Stack>
                    {map(toArray(selectedWorkflow?.data?.clients), o => (
                      <HStack>
                        <Box boxSize={5}>
                          <Center>{getIcon(o.client_name)}</Center>
                        </Box>
                        <Box>{o.name}</Box>
                      </HStack>
                    ))}
                  </Stack>
                ),
              },
              note: {
                type: 'custom',
                component: () => (
                  <Box color="primary" fontSize="sm">
                    NOTE: Once active, workflow criteria will be used to execute
                    actions for automated routing to the targets.
                  </Box>
                ),
              },
            }}
            buttonOptions={{
              submit: {
                name: 'OK',
                isLoading: blueprintAction.isLoading,
                isDisabled: blueprintAction.isLoading,
              },
              reset: {
                name: 'CANCEL',
                isVisible: true,
                onClick: () => onClose(),
              },
            }}
            submitButtonRef={okButtonRef}
            handleSubmit={handleSubmit}
            styles={{
              form: {
                submitButton: {
                  px: 6,
                  autoFocus: true,
                  onKeyDown: handleOkKeyDown,
                },
              },
            }}
          />
        </>
      }
      size="lg"
    />
  );
};
